import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import { Button, Grid, Link, Paper, Stack, Typography } from '@mui/material';
import { Box, Container } from '@mui/system';

import DynamicBreadcrumbs from '@/components/nav/Breadcrumb';
import AppLayout from '@/layouts/AppLayout';
import { Meta } from '@/layouts/Meta';

const BusinessTax = () => {
  return (
    <AppLayout
      meta={
        <Meta
          title="Tax Extension Resource center | Essential Filing Information"
          description="Explore Form 7004, 4868, 8868, 8809 and 15397 resources, including instructions, due dates, filing guidance, bulk uploads, and more."
          keywords=""
          canonical="resources/tax-extension/"
          ogType="article"
          jsonLdPricing="17.99"
          jsonLdAggregateRatingValue="4.95"
          jsonLdAggregateRatingCount="10859"
        />
      }
    >
      <Container
        maxWidth="lg"
        sx={{ justifyContent: 'center', mt: 8, mb: 8 }}
        component="section"
      >
        <Typography
          variant="h1"
          sx={{
            fontSize: { lg: 40, md: 36, sm: 32, xs: 24 },
            fontWeight: 600,
            mb: 2,
            mt: 5,
            textAlign: 'center',
          }}
        >
          <span className="highlight-container">
            <span className="highlight">Tax Extension </span>
          </span>{' '}
          Resource Center
        </Typography>

        <Typography
          variant="body1"
          sx={{
            fontSize: { md: 18, sm: 16, xs: 16 },
            textAlign: 'center',
            mb: 1.5,
          }}
        >
          Find clear guidance on tax extension forms, filing deadlines,
          instructions, and related requirements.
        </Typography>
        <Typography
          variant="body1"
          sx={{
            fontSize: { md: 18, sm: 16, xs: 16 },
            textAlign: 'center',
            mb: 3,
          }}
        >
          Explore TaxZerone's tax extension resources for{' '}
          <strong style={{ fontWeight: 600 }}>
            Form 7004, Form 4868, Form 8868, Form 8809, and Form 15397
          </strong>
          , including instructions, due dates, filing guidance, and related
          resources.
        </Typography>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <Button
            variant="contained"
            className="btn-primary medium"
            sx={{
              fontSize: {
                xs: '16px !important',
                sm: '18px !important',
                lg: '24px !important',
              },
              transition: 'all 0.3s ease 0s',
              '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
            }}
            href="#explore"
          >
            Explore Tax Extension Resources
          </Button>
        </Box>
      </Container>
      <Grid
        container
        sx={{
          backgroundColor: '#f0f2f9',
          py: 5,
          mx: '0 !important',
          width: '100% !important',
          scrollMarginTop: 64,
        }}
        spacing={2}
        m={0}
        mt={0}
        component="section"
        id="explore"
      >
        <Container maxWidth="lg" sx={{ mb: 0 }}>
          <DynamicBreadcrumbs />
          <Grid
            container
            spacing={3}
            rowSpacing={4}
            m={0}
            sx={{ justifyContent: 'center' }}
          >
            <Grid size={{ xs: 12, md: 12, sm: 12 }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 30, md: 24, sm: 20, xs: 18 },
                  fontWeight: 600,
                  mb: 0.5,
                }}
              >
                Business Tax Extension
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                Explore Form 7004 resources covering filing instructions, due
                dates, form codes, supported returns, bulk uploads, and
                extension calculations.
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Link
                href="/resources/tax-extension/form-7004-instructions/"
                sx={{ textDecoration: 'none' }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    px: 2,
                    position: 'relative',
                    backgroundColor: '#f9f9f9',
                    borderRadius: 3,
                    pt: 3,
                    pb: 2,
                    minHeight: '100%',
                    justifyContent: 'space-around',
                    alignItems: 'center',
                    transition:
                      'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                      transform: 'rotate(-225deg)',
                    },
                    '&:hover': {
                      boxShadow: ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                      transform: 'translateY(-0.1rem)',
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      zIndex: 2,
                      position: 'absolute',
                      fontSize: { md: 13, sm: 12, xs: 11 },
                      borderRadius: 24,
                      backgroundColor: '#eff3ff',
                      color: '#223d99',
                      fontWeight: 500,
                      border: '1px solid #223d99',
                      px: 2,
                      display: 'inline-block',
                      top: -12,
                      left: 15,
                    }}
                  >
                    Essentials
                  </Typography>

                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 22, sm: 19, xs: 16 },
                        mb: 0.5,
                      }}
                    >
                      Form 7004 Instructions
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      Get clear guidance on Form 7004 filing requirements,
                      eligibility, and how to request an extension.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'left',
                      alignItems: 'left',
                      textAlign: 'left',
                      gap: 1.2,
                      color: '#0202ff',
                    }}
                  >
                    <Typography variant="body1">
                      Read Form 7004 Instructions
                    </Typography>
                    <KeyboardBackspaceIcon
                      sx={{
                        transform: 'rotate(-180deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  </Box>
                </Paper>
              </Link>
            </Grid>

            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Link
                href="/resources/tax-extension/form-7004-due-dates/"
                sx={{ textDecoration: 'none' }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    px: 2,
                    position: 'relative',
                    backgroundColor: '#f9f9f9',
                    borderRadius: 3,
                    pt: 3,
                    pb: 2,
                    minHeight: '100%',
                    justifyContent: 'space-around',
                    alignItems: 'center',
                    transition:
                      'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                      transform: 'rotate(-225deg)',
                    },
                    '&:hover': {
                      boxShadow: ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                      transform: 'translateY(-0.1rem)',
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      zIndex: 2,
                      position: 'absolute',
                      fontSize: { md: 13, sm: 12, xs: 11 },
                      borderRadius: 24,
                      backgroundColor: '#eff3ff',
                      color: '#223d99',
                      fontWeight: 500,
                      border: '1px solid #223d99',
                      px: 2,
                      display: 'inline-block',
                      top: -12,
                      left: 15,
                    }}
                  >
                    Important dates
                  </Typography>

                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 22, sm: 19, xs: 16 },
                        mb: 0.5,
                      }}
                    >
                      Form 7004 Due Dates
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      Find the Form 7004 deadline based on the tax return you're
                      extending and understand the applicable extension period.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'left',
                      alignItems: 'left',
                      textAlign: 'left',
                      gap: 1.2,
                      color: '#0202ff',
                    }}
                  >
                    <Typography variant="body1">Check Due Dates</Typography>
                    <KeyboardBackspaceIcon
                      sx={{
                        transform: 'rotate(-180deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  </Box>
                </Paper>
              </Link>
            </Grid>

            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Link
                href="/resources/tax-extension/form-7004-bulk-upload/"
                sx={{ textDecoration: 'none' }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    px: 2,
                    position: 'relative',
                    backgroundColor: '#f9f9f9',
                    borderRadius: 3,
                    pt: 3,
                    pb: 2,
                    minHeight: '100%',
                    justifyContent: 'space-around',
                    alignItems: 'center',
                    transition:
                      'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                      transform: 'rotate(-225deg)',
                    },
                    '&:hover': {
                      boxShadow: ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                      transform: 'translateY(-0.1rem)',
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      zIndex: 2,
                      position: 'absolute',
                      fontSize: { md: 13, sm: 12, xs: 11 },
                      borderRadius: 24,
                      backgroundColor: '#eff3ff',
                      color: '#223d99',
                      fontWeight: 500,
                      border: '1px solid #223d99',
                      px: 2,
                      display: 'inline-block',
                      top: -12,
                      left: 15,
                    }}
                  >
                    Most helpful
                  </Typography>

                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 22, sm: 19, xs: 16 },
                        mb: 0.5,
                      }}
                    >
                      Form 7004 Bulk Upload
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      Upload your Form 7004 filing details through an Excel file
                      and easily prepare multiple extension filings at once.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'left',
                      alignItems: 'left',
                      textAlign: 'left',
                      gap: 1.2,
                      color: '#0202ff',
                    }}
                  >
                    <Typography variant="body1">
                      Learn About Bulk Upload
                    </Typography>
                    <KeyboardBackspaceIcon
                      sx={{
                        transform: 'rotate(-180deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  </Box>
                </Paper>
              </Link>
            </Grid>

            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Link
                href="/resources/tax-extension/form-7004-for-form-1042/"
                sx={{ textDecoration: 'none' }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    px: 2,
                    position: 'relative',
                    backgroundColor: '#f9f9f9',
                    borderRadius: 3,
                    pt: 3,
                    pb: 2,
                    minHeight: '100%',
                    justifyContent: 'space-around',
                    alignItems: 'center',
                    transition:
                      'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                      transform: 'rotate(-225deg)',
                    },
                    '&:hover': {
                      boxShadow: ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                      transform: 'translateY(-0.1rem)',
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      zIndex: 2,
                      position: 'absolute',
                      fontSize: { md: 13, sm: 12, xs: 11 },
                      borderRadius: 24,
                      backgroundColor: '#eff3ff',
                      color: '#223d99',
                      fontWeight: 500,
                      border: '1px solid #223d99',
                      px: 2,
                      display: 'inline-block',
                      top: -12,
                      left: 15,
                    }}
                  >
                    Form-Specific Guidance
                  </Typography>

                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 22, sm: 19, xs: 16 },
                        mb: 0.5,
                      }}
                    >
                      Form 7004 for Form 1042
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      Learn how Form 7004 applies when requesting an extension
                      for Form 1042.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'left',
                      alignItems: 'left',
                      textAlign: 'left',
                      gap: 1.2,
                      color: '#0202ff',
                    }}
                  >
                    <Typography variant="body1">
                      Learn About Form 1042
                    </Typography>
                    <KeyboardBackspaceIcon
                      sx={{
                        transform: 'rotate(-180deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  </Box>
                </Paper>
              </Link>
            </Grid>

            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Link
                href="/resources/tax-extension/form-7004-for-form-1065/"
                sx={{ textDecoration: 'none' }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    px: 2,
                    position: 'relative',
                    backgroundColor: '#f9f9f9',
                    borderRadius: 3,
                    pt: 3,
                    pb: 2,
                    minHeight: '100%',
                    justifyContent: 'space-around',
                    alignItems: 'center',
                    transition:
                      'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                      transform: 'rotate(-225deg)',
                    },
                    '&:hover': {
                      boxShadow: ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                      transform: 'translateY(-0.1rem)',
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      zIndex: 2,
                      position: 'absolute',
                      fontSize: { md: 13, sm: 12, xs: 11 },
                      borderRadius: 24,
                      backgroundColor: '#eff3ff',
                      color: '#223d99',
                      fontWeight: 500,
                      border: '1px solid #223d99',
                      px: 2,
                      display: 'inline-block',
                      top: -12,
                      left: 15,
                    }}
                  >
                    Form-Specific Guidance
                  </Typography>

                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 22, sm: 19, xs: 16 },
                        mb: 0.5,
                      }}
                    >
                      Form 7004 for Form 1065
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      Find Form 7004 extension information specific to
                      partnerships filing Form 1065.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'left',
                      alignItems: 'left',
                      textAlign: 'left',
                      gap: 1.2,
                      color: '#0202ff',
                    }}
                  >
                    <Typography variant="body1">
                      Learn About Form 1065
                    </Typography>
                    <KeyboardBackspaceIcon
                      sx={{
                        transform: 'rotate(-180deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  </Box>
                </Paper>
              </Link>
            </Grid>

            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Link
                href="/resources/tax-extension/form-7004-for-form-1120/"
                sx={{ textDecoration: 'none' }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    px: 2,
                    position: 'relative',
                    backgroundColor: '#f9f9f9',
                    borderRadius: 3,
                    pt: 3,
                    pb: 2,
                    minHeight: '100%',
                    justifyContent: 'space-around',
                    alignItems: 'center',
                    transition:
                      'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                      transform: 'rotate(-225deg)',
                    },
                    '&:hover': {
                      boxShadow: ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                      transform: 'translateY(-0.1rem)',
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      zIndex: 2,
                      position: 'absolute',
                      fontSize: { md: 13, sm: 12, xs: 11 },
                      borderRadius: 24,
                      backgroundColor: '#eff3ff',
                      color: '#223d99',
                      fontWeight: 500,
                      border: '1px solid #223d99',
                      px: 2,
                      display: 'inline-block',
                      top: -12,
                      left: 15,
                    }}
                  >
                    Form-Specific Guidance
                  </Typography>

                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 22, sm: 19, xs: 16 },
                        mb: 0.5,
                      }}
                    >
                      Form 7004 for Form 1120
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      Understand how to use Form 7004 to request an extension
                      for eligible Form 1120 returns.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'left',
                      alignItems: 'left',
                      textAlign: 'left',
                      gap: 1.2,
                      color: '#0202ff',
                    }}
                  >
                    <Typography variant="body1">
                      Learn About Form 1120
                    </Typography>
                    <KeyboardBackspaceIcon
                      sx={{
                        transform: 'rotate(-180deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  </Box>
                </Paper>
              </Link>
            </Grid>

            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Link
                href="/resources/tax-extension/form-7004-form-codes/"
                sx={{ textDecoration: 'none' }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    px: 2,
                    position: 'relative',
                    backgroundColor: '#f9f9f9',
                    borderRadius: 3,
                    pt: 3,
                    pb: 2,
                    minHeight: '100%',
                    justifyContent: 'space-around',
                    alignItems: 'center',
                    transition:
                      'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                      transform: 'rotate(-225deg)',
                    },
                    '&:hover': {
                      boxShadow: ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                      transform: 'translateY(-0.1rem)',
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      zIndex: 2,
                      position: 'absolute',
                      fontSize: { md: 13, sm: 12, xs: 11 },
                      borderRadius: 24,
                      backgroundColor: '#eff3ff',
                      color: '#223d99',
                      fontWeight: 500,
                      border: '1px solid #223d99',
                      px: 2,
                      display: 'inline-block',
                      top: -12,
                      left: 15,
                    }}
                  >
                    Form Codes
                  </Typography>

                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 22, sm: 19, xs: 16 },
                        mb: 0.5,
                      }}
                    >
                      Form 7004 Form Codes
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      Find the form codes needed when completing Form 7004 for
                      different tax returns.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'left',
                      alignItems: 'left',
                      textAlign: 'left',
                      gap: 1.2,
                      color: '#0202ff',
                    }}
                  >
                    <Typography variant="body1">Find Form Codes</Typography>
                    <KeyboardBackspaceIcon
                      sx={{
                        transform: 'rotate(-180deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  </Box>
                </Paper>
              </Link>
            </Grid>

            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Link
                href="/tools/form-7004-due-date-calculator/"
                sx={{ textDecoration: 'none' }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    px: 2,
                    position: 'relative',
                    backgroundColor: '#f9f9f9',
                    borderRadius: 3,
                    pt: 3,
                    pb: 2,
                    minHeight: '100%',
                    justifyContent: 'space-around',
                    alignItems: 'center',
                    transition:
                      'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                      transform: 'rotate(-225deg)',
                    },
                    '&:hover': {
                      boxShadow: ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                      transform: 'translateY(-0.1rem)',
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      zIndex: 2,
                      position: 'absolute',
                      fontSize: { md: 13, sm: 12, xs: 11 },
                      borderRadius: 24,
                      backgroundColor: '#eff3ff',
                      color: '#223d99',
                      fontWeight: 500,
                      border: '1px solid #223d99',
                      px: 2,
                      display: 'inline-block',
                      top: -12,
                      left: 15,
                    }}
                  >
                    Helpful Tool
                  </Typography>

                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 22, sm: 19, xs: 16 },
                        mb: 0.5,
                      }}
                    >
                      Form 7004 Due Date Calculator
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      Calculate your Form 7004 extension deadline based on your
                      tax return and tax year.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'left',
                      alignItems: 'left',
                      textAlign: 'left',
                      gap: 1.2,
                      color: '#0202ff',
                    }}
                  >
                    <Typography variant="body1">Calculate Due Date</Typography>
                    <KeyboardBackspaceIcon
                      sx={{
                        transform: 'rotate(-180deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  </Box>
                </Paper>
              </Link>
            </Grid>
          </Grid>
        </Container>
      </Grid>

      <Box component="section" sx={{ my: 8 }}>
        <Container maxWidth="lg">
          <Grid
            container
            spacing={3}
            rowSpacing={4}
            m={0}
            sx={{ justifyContent: 'center' }}
          >
            <Grid size={{ xs: 12, md: 12, sm: 12 }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 30, md: 24, sm: 20, xs: 18 },
                  fontWeight: 600,
                  mb: 0.5,
                }}
              >
                Individual Tax Extension
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                Explore Form 4868 resources covering instructions, filing
                deadlines, bulk uploads, and mailing information.
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Link
                href="/resources/tax-extension/form-4868-instructions/"
                sx={{ textDecoration: 'none' }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    px: 2,
                    position: 'relative',
                    backgroundColor: '#f9f9f9',
                    borderRadius: 3,
                    pt: 3,
                    pb: 2,
                    minHeight: '100%',
                    justifyContent: 'space-around',
                    alignItems: 'center',
                    transition:
                      'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                      transform: 'rotate(-225deg)',
                    },
                    '&:hover': {
                      boxShadow: ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                      transform: 'translateY(-0.1rem)',
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      zIndex: 2,
                      position: 'absolute',
                      fontSize: { md: 13, sm: 12, xs: 11 },
                      borderRadius: 24,
                      backgroundColor: '#eff3ff',
                      color: '#223d99',
                      fontWeight: 500,
                      border: '1px solid #223d99',
                      px: 2,
                      display: 'inline-block',
                      top: -12,
                      left: 15,
                    }}
                  >
                    Essentials
                  </Typography>

                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 22, sm: 19, xs: 16 },
                        mb: 0.5,
                      }}
                    >
                      Form 4868 Instructions
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      Understand the filing requirements and instructions for
                      requesting an individual tax extension.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'left',
                      alignItems: 'left',
                      textAlign: 'left',
                      gap: 1.2,
                      color: '#0202ff',
                    }}
                  >
                    <Typography variant="body1">
                      Read 4868 Instructions
                    </Typography>
                    <KeyboardBackspaceIcon
                      sx={{
                        transform: 'rotate(-180deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  </Box>
                </Paper>
              </Link>
            </Grid>

            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Link
                href="/resources/tax-extension/form-4868-bulk-upload/"
                sx={{ textDecoration: 'none' }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    px: 2,
                    position: 'relative',
                    backgroundColor: '#f9f9f9',
                    borderRadius: 3,
                    pt: 3,
                    pb: 2,
                    minHeight: '100%',
                    justifyContent: 'space-around',
                    alignItems: 'center',
                    transition:
                      'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                      transform: 'rotate(-225deg)',
                    },
                    '&:hover': {
                      boxShadow: ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                      transform: 'translateY(-0.1rem)',
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      zIndex: 2,
                      position: 'absolute',
                      fontSize: { md: 13, sm: 12, xs: 11 },
                      borderRadius: 24,
                      backgroundColor: '#eff3ff',
                      color: '#223d99',
                      fontWeight: 500,
                      border: '1px solid #223d99',
                      px: 2,
                      display: 'inline-block',
                      top: -12,
                      left: 15,
                    }}
                  >
                    Most helpful
                  </Typography>

                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 22, sm: 19, xs: 16 },
                        mb: 0.5,
                      }}
                    >
                      Form 4868 Bulk Upload
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      Easily upload Form 4868 details using an Excel file and
                      streamline multiple extension filings in one go.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'left',
                      alignItems: 'left',
                      textAlign: 'left',
                      gap: 1.2,
                      color: '#0202ff',
                    }}
                  >
                    <Typography variant="body1">
                      Learn About Bulk Upload
                    </Typography>
                    <KeyboardBackspaceIcon
                      sx={{
                        transform: 'rotate(-180deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  </Box>
                </Paper>
              </Link>
            </Grid>

            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Link
                href="/resources/tax-extension/form-4868-due-dates/"
                sx={{ textDecoration: 'none' }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    px: 2,
                    position: 'relative',
                    backgroundColor: '#f9f9f9',
                    borderRadius: 3,
                    pt: 3,
                    pb: 2,
                    minHeight: '100%',
                    justifyContent: 'space-around',
                    alignItems: 'center',
                    transition:
                      'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                      transform: 'rotate(-225deg)',
                    },
                    '&:hover': {
                      boxShadow: ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                      transform: 'translateY(-0.1rem)',
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      zIndex: 2,
                      position: 'absolute',
                      fontSize: { md: 13, sm: 12, xs: 11 },
                      borderRadius: 24,
                      backgroundColor: '#eff3ff',
                      color: '#223d99',
                      fontWeight: 500,
                      border: '1px solid #223d99',
                      px: 2,
                      display: 'inline-block',
                      top: -12,
                      left: 15,
                    }}
                  >
                    Important dates
                  </Typography>

                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 22, sm: 19, xs: 16 },
                        mb: 0.5,
                      }}
                    >
                      Form 4868 Due Dates
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      Find important Form 4868 deadlines and understand when
                      your extension request must be filed.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'left',
                      alignItems: 'left',
                      textAlign: 'left',
                      gap: 1.2,
                      color: '#0202ff',
                    }}
                  >
                    <Typography variant="body1">View Due Dates</Typography>
                    <KeyboardBackspaceIcon
                      sx={{
                        transform: 'rotate(-180deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  </Box>
                </Paper>
              </Link>
            </Grid>

            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Link
                href="/resources/tax-extension/form-4868-mailing-addresses-and-filing-guide/"
                sx={{ textDecoration: 'none' }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    px: 2,
                    position: 'relative',
                    backgroundColor: '#f9f9f9',
                    borderRadius: 3,
                    pt: 3,
                    pb: 2,
                    minHeight: '100%',
                    justifyContent: 'space-around',
                    alignItems: 'center',
                    transition:
                      'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                      transform: 'rotate(-225deg)',
                    },
                    '&:hover': {
                      boxShadow: ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                      transform: 'translateY(-0.1rem)',
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      zIndex: 2,
                      position: 'absolute',
                      fontSize: { md: 13, sm: 12, xs: 11 },
                      borderRadius: 24,
                      backgroundColor: '#eff3ff',
                      color: '#223d99',
                      fontWeight: 500,
                      border: '1px solid #223d99',
                      px: 2,
                      display: 'inline-block',
                      top: -12,
                      left: 15,
                    }}
                  >
                    Mailing Information
                  </Typography>

                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 22, sm: 19, xs: 16 },
                        mb: 0.5,
                      }}
                    >
                      Form 4868 Mailing Address
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      Find the appropriate mailing address and requirements when
                      filing Form 4868 by mail.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'left',
                      alignItems: 'left',
                      textAlign: 'left',
                      gap: 1.2,
                      color: '#0202ff',
                    }}
                  >
                    <Typography variant="body1">
                      Check Mailing Address
                    </Typography>
                    <KeyboardBackspaceIcon
                      sx={{
                        transform: 'rotate(-180deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  </Box>
                </Paper>
              </Link>
            </Grid>

            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Link
                href="/resources/tax-extension/form-7004-and-4868-comparison/"
                sx={{ textDecoration: 'none' }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    px: 2,
                    position: 'relative',
                    backgroundColor: '#f9f9f9',
                    borderRadius: 3,
                    pt: 3,
                    pb: 2,
                    minHeight: '100%',
                    justifyContent: 'space-around',
                    alignItems: 'center',
                    transition:
                      'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                      transform: 'rotate(-225deg)',
                    },
                    '&:hover': {
                      boxShadow: ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                      transform: 'translateY(-0.1rem)',
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      zIndex: 2,
                      position: 'absolute',
                      fontSize: { md: 13, sm: 12, xs: 11 },
                      borderRadius: 24,
                      backgroundColor: '#eff3ff',
                      color: '#223d99',
                      fontWeight: 500,
                      border: '1px solid #223d99',
                      px: 2,
                      display: 'inline-block',
                      top: -12,
                      left: 15,
                    }}
                  >
                    Form Comparison
                  </Typography>

                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 22, sm: 19, xs: 16 },
                        mb: 0.5,
                      }}
                    >
                      Form 7004 vs. Form 4868
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      Compare Form 7004 and Form 4868 to determine which tax
                      extension form applies to your filing situation.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'left',
                      alignItems: 'left',
                      textAlign: 'left',
                      gap: 1.2,
                      color: '#0202ff',
                    }}
                  >
                    <Typography variant="body1">Compare the Forms</Typography>
                    <KeyboardBackspaceIcon
                      sx={{
                        transform: 'rotate(-180deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  </Box>
                </Paper>
              </Link>
            </Grid>
          </Grid>
        </Container>
      </Box>
      <Box component="section" sx={{ backgroundColor: '#f0f2f9' }}>
        <Container maxWidth="lg" sx={{ py: 8 }}>
          <Grid
            container
            spacing={3}
            rowSpacing={4}
            m={0}
            sx={{ justifyContent: 'center' }}
          >
            <Grid size={{ xs: 12, md: 12, sm: 12 }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 30, md: 24, sm: 20, xs: 18 },
                  fontWeight: 600,
                  mb: 0.5,
                }}
              >
                Tax-Exempt Organization Extension
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                Find Form 8868 resources for tax-exempt organizations, including
                filing instructions and important extension deadlines.
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Link
                href="/resources/tax-extension/form-8868-instructions/"
                sx={{ textDecoration: 'none' }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    px: 2,
                    position: 'relative',
                    backgroundColor: '#f9f9f9',
                    borderRadius: 3,
                    pt: 3,
                    pb: 2,
                    minHeight: '100%',
                    justifyContent: 'space-around',
                    alignItems: 'center',
                    transition:
                      'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                      transform: 'rotate(-225deg)',
                    },
                    '&:hover': {
                      boxShadow: ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                      transform: 'translateY(-0.1rem)',
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      zIndex: 2,
                      position: 'absolute',
                      fontSize: { md: 13, sm: 12, xs: 11 },
                      borderRadius: 24,
                      backgroundColor: '#eff3ff',
                      color: '#223d99',
                      fontWeight: 500,
                      border: '1px solid #223d99',
                      px: 2,
                      display: 'inline-block',
                      top: -12,
                      left: 15,
                    }}
                  >
                    Essentials
                  </Typography>

                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 22, sm: 19, xs: 16 },
                        mb: 0.5,
                      }}
                    >
                      Form 8868 Instructions
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      Understand the requirements and instructions for
                      completing and filing Form 8868.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'left',
                      alignItems: 'left',
                      textAlign: 'left',
                      gap: 1.2,
                      color: '#0202ff',
                    }}
                  >
                    <Typography variant="body1">
                      Read 8868 Instructions
                    </Typography>
                    <KeyboardBackspaceIcon
                      sx={{
                        transform: 'rotate(-180deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  </Box>
                </Paper>
              </Link>
            </Grid>

            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Link
                href="/resources/tax-extension/form-8868-due-dates/"
                sx={{ textDecoration: 'none' }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    px: 2,
                    position: 'relative',
                    backgroundColor: '#f9f9f9',
                    borderRadius: 3,
                    pt: 3,
                    pb: 2,
                    minHeight: '100%',
                    justifyContent: 'space-around',
                    alignItems: 'center',
                    transition:
                      'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                      transform: 'rotate(-225deg)',
                    },
                    '&:hover': {
                      boxShadow: ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                      transform: 'translateY(-0.1rem)',
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      zIndex: 2,
                      position: 'absolute',
                      fontSize: { md: 13, sm: 12, xs: 11 },
                      borderRadius: 24,
                      backgroundColor: '#eff3ff',
                      color: '#223d99',
                      fontWeight: 500,
                      border: '1px solid #223d99',
                      px: 2,
                      display: 'inline-block',
                      top: -12,
                      left: 15,
                    }}
                  >
                    Important Dates
                  </Typography>

                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 22, sm: 19, xs: 16 },
                        mb: 0.5,
                      }}
                    >
                      Form 8868 Due Dates
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      Find Form 8868 filing deadlines and learn when to submit
                      an extension request.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'left',
                      alignItems: 'left',
                      textAlign: 'left',
                      gap: 1.2,
                      color: '#0202ff',
                    }}
                  >
                    <Typography variant="body1">View Due Date</Typography>
                    <KeyboardBackspaceIcon
                      sx={{
                        transform: 'rotate(-180deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  </Box>
                </Paper>
              </Link>
            </Grid>
          </Grid>
        </Container>
      </Box>
      <Box component="section" sx={{ backgroundColor: '#fff' }}>
        <Container maxWidth="lg" sx={{ py: 8 }}>
          <Grid
            container
            spacing={3}
            rowSpacing={4}
            m={0}
            sx={{ justifyContent: 'center' }}
          >
            <Grid size={{ xs: 12, md: 12, sm: 12 }}>
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 30, md: 24, sm: 20, xs: 18 },
                  fontWeight: 600,
                  mb: 0.5,
                }}
              >
                Information Return Extensions
              </Typography>
              <Typography variant="body1" sx={{ mb: 1 }}>
                Explore resources for extending eligible information returns,
                including Form 8809 instructions and deadlines and Form 15397
                filing guidance.
              </Typography>
            </Grid>
            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Link
                href="/resources/tax-extension/form-8809-instructions/"
                sx={{ textDecoration: 'none' }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    px: 2,
                    position: 'relative',
                    backgroundColor: '#f9f9f9',
                    borderRadius: 3,
                    pt: 3,
                    pb: 2,
                    minHeight: '100%',
                    justifyContent: 'space-around',
                    alignItems: 'center',
                    transition:
                      'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                      transform: 'rotate(-225deg)',
                    },
                    '&:hover': {
                      boxShadow: ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                      transform: 'translateY(-0.1rem)',
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      zIndex: 2,
                      position: 'absolute',
                      fontSize: { md: 13, sm: 12, xs: 11 },
                      borderRadius: 24,
                      backgroundColor: '#eff3ff',
                      color: '#223d99',
                      fontWeight: 500,
                      border: '1px solid #223d99',
                      px: 2,
                      display: 'inline-block',
                      top: -12,
                      left: 15,
                    }}
                  >
                    Essentials
                  </Typography>

                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 22, sm: 19, xs: 16 },
                        mb: 0.5,
                      }}
                    >
                      Form 8809 Instructions
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      Get guidance on eligibility, filing requirements, and
                      completing Form 8809.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'left',
                      alignItems: 'left',
                      textAlign: 'left',
                      gap: 1.2,
                      color: '#0202ff',
                    }}
                  >
                    <Typography variant="body1">Read Instructions</Typography>
                    <KeyboardBackspaceIcon
                      sx={{
                        transform: 'rotate(-180deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  </Box>
                </Paper>
              </Link>
            </Grid>

            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Link
                href="/resources/tax-extension/form-8809-due-dates/"
                sx={{ textDecoration: 'none' }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    px: 2,
                    position: 'relative',
                    backgroundColor: '#f9f9f9',
                    borderRadius: 3,
                    pt: 3,
                    pb: 2,
                    minHeight: '100%',
                    justifyContent: 'space-around',
                    alignItems: 'center',
                    transition:
                      'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                      transform: 'rotate(-225deg)',
                    },
                    '&:hover': {
                      boxShadow: ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                      transform: 'translateY(-0.1rem)',
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      zIndex: 2,
                      position: 'absolute',
                      fontSize: { md: 13, sm: 12, xs: 11 },
                      borderRadius: 24,
                      backgroundColor: '#eff3ff',
                      color: '#223d99',
                      fontWeight: 500,
                      border: '1px solid #223d99',
                      px: 2,
                      display: 'inline-block',
                      top: -12,
                      left: 15,
                    }}
                  >
                    Important Dates
                  </Typography>

                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 22, sm: 19, xs: 16 },
                        mb: 0.5,
                      }}
                    >
                      Form 8809 Due Dates
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      Find important deadlines for submitting Form 8809 and
                      requesting an information return extension.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'left',
                      alignItems: 'left',
                      textAlign: 'left',
                      gap: 1.2,
                      color: '#0202ff',
                    }}
                  >
                    <Typography variant="body1">Check 8809 Due Date</Typography>
                    <KeyboardBackspaceIcon
                      sx={{
                        transform: 'rotate(-180deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  </Box>
                </Paper>
              </Link>
            </Grid>

            <Grid size={{ xs: 12, md: 6, sm: 6 }}>
              <Link
                href="/resources/tax-extension/form-15397-instructions/"
                sx={{ textDecoration: 'none' }}
              >
                <Paper
                  variant="outlined"
                  sx={{
                    px: 2,
                    position: 'relative',
                    backgroundColor: '#f9f9f9',
                    borderRadius: 3,
                    pt: 3,
                    pb: 2,
                    minHeight: '100%',
                    justifyContent: 'space-around',
                    alignItems: 'center',
                    transition:
                      'transform 0.3s ease-in-out, box-shadow 0.3s ease-in-out',
                    '&:hover .css-e1jxyz-MuiSvgIcon-root': {
                      transform: 'rotate(-225deg)',
                    },
                    '&:hover': {
                      boxShadow: ' rgba(100, 100, 111, 0.2) 0px 7px 29px 0px',
                      transform: 'translateY(-0.1rem)',
                    },
                  }}
                >
                  <Typography
                    variant="body1"
                    sx={{
                      transition: 'transform 0.3s ease, box-shadow 0.3s ease',
                      zIndex: 2,
                      position: 'absolute',
                      fontSize: { md: 13, sm: 12, xs: 11 },
                      borderRadius: 24,
                      backgroundColor: '#eff3ff',
                      color: '#223d99',
                      fontWeight: 500,
                      border: '1px solid #223d99',
                      px: 2,
                      display: 'inline-block',
                      top: -12,
                      left: 15,
                    }}
                  >
                    Essentials
                  </Typography>

                  <Box>
                    <Typography
                      variant="h3"
                      sx={{
                        fontWeight: 600,
                        fontSize: { md: 22, sm: 19, xs: 16 },
                        mb: 0.5,
                      }}
                    >
                      Form 15397 Instructions
                    </Typography>
                    <Typography variant="body1" sx={{ mb: 3 }}>
                      Understand the filing requirements and instructions
                      associated with Form 15397.
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'flex',
                      justifyContent: 'left',
                      alignItems: 'left',
                      textAlign: 'left',
                      gap: 1.2,
                      color: '#0202ff',
                    }}
                  >
                    <Typography variant="body1">Read Instructions</Typography>
                    <KeyboardBackspaceIcon
                      sx={{
                        transform: 'rotate(-180deg)',
                        transition: 'transform 0.3s ease',
                      }}
                    />
                  </Box>
                </Paper>
              </Link>
            </Grid>
          </Grid>
        </Container>
      </Box>

      <Box component="section" sx={{ backgroundColor: '#f0f2f9' }}>
        <Container maxWidth="lg" sx={{ py: 8 }}>
          <Stack
            direction={{ xs: 'column', md: 'row' }}
            spacing={{ xs: 2, md: 5 }}
          >
            <Box
              sx={{
                width: '100%',
                textAlign: 'center',
              }}
            >
              <Typography
                variant="h2"
                sx={{
                  fontSize: { lg: 34, md: 30, sm: 26, xs: 22 },
                  fontWeight: 600,
                  mb: 3,
                  textAlign: 'center',
                  lineHeight: 1.5,
                }}
              >
                Need Help Filing Business Taxes?
              </Typography>
              <Typography
                variant="body1"
                sx={{
                  fontSize: { lg: 18, md: 18, sm: 16, xs: 16 },
                  fontWeight: 400,
                  color: '#333333',
                  mb: 2,
                }}
              >
                Have the information you need? File your business tax return
                online with a guided e-filing experience
                <br /> designed to make filing simple and accurate.
              </Typography>

              <Box sx={{ textAlign: { xs: 'center', md: 'center' } }}>
                <Button
                  variant="contained"
                  sx={{
                    mt: 1,
                    transition: 'all 0.3s ease 0s',
                    '&:hover, &:focus, &:active': { transform: 'scale(1.1)' },
                  }}
                  className="btn-primary"
                  href={`${process.env.NEXT_PUBLIC_APPLICATION_URL}signup/?ref=tax-extension`}
                >
                  Get Started Now
                </Button>
              </Box>
            </Box>
          </Stack>
        </Container>
      </Box>
    </AppLayout>
  );
};

export default BusinessTax;
